import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * GPS Source Types
 */
export const GPS_SOURCE = {
  NONE: 'none',
  EXTERNAL: 'external',  // CrewZ case GPS (u-blox SAM-M10Q, 25Hz multi-GNSS via BLE)
  PHONE: 'phone',        // Phone's built-in GPS via Geolocation API
};

/**
 * Parse NMEA sentences from external GPS
 */
function parseNMEA(sentence) {
  if (!sentence) return null;
  
  try {
    // $GPGGA - GPS Fix Data
    if (sentence.startsWith('$GPGGA') || sentence.startsWith('$GNGGA')) {
      const parts = sentence.split(',');
      if (parts.length < 10) return null;
      
      const lat = parseNMEACoord(parts[2], parts[3]);
      const lng = parseNMEACoord(parts[4], parts[5]);
      const altitude = parseFloat(parts[9]) || 0;
      const satellites = parseInt(parts[7]) || 0;
      
      if (lat && lng) {
        return { lat, lng, altitude, satellites, source: GPS_SOURCE.EXTERNAL };
      }
    }
    
    // $GPRMC - Recommended Minimum (has speed)
    if (sentence.startsWith('$GPRMC') || sentence.startsWith('$GNRMC')) {
      const parts = sentence.split(',');
      if (parts.length < 8) return null;
      
      const lat = parseNMEACoord(parts[3], parts[4]);
      const lng = parseNMEACoord(parts[5], parts[6]);
      const speedKnots = parseFloat(parts[7]) || 0;
      const speed = speedKnots * 0.514444; // Convert to m/s
      
      if (lat && lng) {
        return { lat, lng, speed, source: GPS_SOURCE.EXTERNAL };
      }
    }
  } catch (e) {
    console.error('NMEA parse error:', e);
  }
  
  return null;
}

/**
 * Convert NMEA coordinates (DDMM.MMMMM) to decimal degrees
 */
function parseNMEACoord(coord, direction) {
  if (!coord || !direction) return null;
  
  const deg = Math.floor(parseFloat(coord) / 100);
  const min = parseFloat(coord) % 100;
  let decimal = deg + (min / 60);
  
  if (direction === 'S' || direction === 'W') {
    decimal = -decimal;
  }
  
  return decimal;
}

/**
 * Calculate distance between two GPS points using Haversine formula
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

// Speed floor (m/s) below which we consider the boat stationary. GPS drift
// while parked at the dock can add several metres a minute via Haversine
// noise — Brief D requires freezing the cumulative distance accumulator in
// that regime. 0.3 m/s ≈ 0.6 knots, well below any genuine rowing speed.
const STATIONARY_SPEED_FLOOR_MPS = 0.3;

/**
 * GPS Hook - Handles both the CrewZ case GPS (BLE) and phone's built-in GPS with automatic fallback.
 *
 * Priority:
 * 1. CrewZ case GPS (u-blox SAM-M10Q, 25Hz multi-GNSS, streamed via BLE) - higher accuracy
 * 2. Phone GPS (Geolocation API) - fallback when the CrewZ case is not present
 *
 * The legacy USB-Serial NMEA path is retained for backwards compatibility but
 * is unused in production once the CrewZ case ships - all real-time data
 * arrives via the `crewz-gps-update` window event.
 *
 * @returns {Object} GPS state and controls
 */
export function useGPS() {
  const [position, setPosition] = useState(null);
  const [source, setSource] = useState(GPS_SOURCE.NONE);
  const [error, setError] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [accuracy, setAccuracy] = useState(null);
  const [speed, setSpeed] = useState(null);
  
  // Track total distance
  const [totalDistance, setTotalDistance] = useState(0);
  const lastPositionRef = useRef(null);
  
  // Serial port reference for external GPS
  const serialPortRef = useRef(null);
  const serialReaderRef = useRef(null);
  
  // Geolocation watch ID for phone GPS
  const watchIdRef = useRef(null);
  
  // External GPS timeout - fallback to phone if no data in 5 seconds
  const externalTimeoutRef = useRef(null);
  const lastExternalUpdateRef = useRef(null);

  /**
   * Start phone GPS (Geolocation API)
   */
  const startPhoneGPS = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported on this device');
      return false;
    }
    
    console.log('📱 Starting phone GPS...');
    
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const newPosition = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          altitude: pos.coords.altitude,
          speed: pos.coords.speed,
          source: GPS_SOURCE.PHONE
        };
        
        setPosition(newPosition);
        setSource(GPS_SOURCE.PHONE);
        setAccuracy(pos.coords.accuracy);
        setSpeed(pos.coords.speed);
        setError(null);
        
        // Calculate distance if we have a previous position
        if (lastPositionRef.current) {
          const dist = calculateDistance(
            lastPositionRef.current.lat,
            lastPositionRef.current.lng,
            newPosition.lat,
            newPosition.lng
          );
          // Only add distance if it's reasonable (< 100m per update to filter GPS jumps)
          // AND we're actually moving (Brief D — freeze accumulator while stationary).
          const movingEnough = typeof newPosition.speed === 'number'
            ? newPosition.speed >= STATIONARY_SPEED_FLOOR_MPS
            : true; // no speed reading → trust the position delta
          if (dist < 100 && movingEnough) {
            setTotalDistance(prev => prev + dist);
          }
        }
        lastPositionRef.current = newPosition;
      },
      (err) => {
        console.error('Phone GPS error:', err);
        setError(err.message);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 1000
      }
    );
    
    return true;
  }, []);

  /**
   * Stop phone GPS
   */
  const stopPhoneGPS = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      console.log('📱 Phone GPS stopped');
    }
  }, []);

  /**
   * Handle external GPS NMEA data
   */
  const handleExternalGPSData = useCallback((nmeaSentence) => {
    const parsed = parseNMEA(nmeaSentence);
    if (parsed) {
      lastExternalUpdateRef.current = Date.now();
      
      // If we were using phone GPS, switch to external
      if (source === GPS_SOURCE.PHONE) {
        console.log('🔄 Switching from phone GPS to external GPS');
        stopPhoneGPS();
      }
      
      setPosition(parsed);
      setSource(GPS_SOURCE.EXTERNAL);
      setSpeed(parsed.speed || null);
      setError(null);
      
      // Calculate distance
      if (lastPositionRef.current && parsed.lat && parsed.lng) {
        const dist = calculateDistance(
          lastPositionRef.current.lat,
          lastPositionRef.current.lng,
          parsed.lat,
          parsed.lng
        );
        const movingEnough = typeof parsed.speed === 'number'
          ? parsed.speed >= STATIONARY_SPEED_FLOOR_MPS
          : true;
        if (dist < 50 && movingEnough) { // Tighter filter for 25Hz GPS
          setTotalDistance(prev => prev + dist);
        }
      }
      lastPositionRef.current = parsed;
    }
  }, [source, stopPhoneGPS]);

  /**
   * Connect to external USB GPS via Web Serial API
   */
  const connectExternalGPS = useCallback(async () => {
    if (!navigator.serial) {
      console.log('Web Serial API not supported, using phone GPS');
      return false;
    }
    
    try {
      console.log('🛰️ Connecting to external GPS...');
      const port = await navigator.serial.requestPort();
      await port.open({ baudRate: 9600 });
      
      serialPortRef.current = port;
      
      const reader = port.readable.getReader();
      serialReaderRef.current = reader;
      
      const decoder = new TextDecoder();
      let buffer = '';
      
      // Read loop
      const readLoop = async () => {
        try {
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            
            buffer += decoder.decode(value);
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer
            
            for (const line of lines) {
              const trimmed = line.trim();
              if (trimmed.startsWith('$GP') || trimmed.startsWith('$GN')) {
                handleExternalGPSData(trimmed);
              }
            }
          }
        } catch (err) {
          console.error('External GPS read error:', err);
          // Fallback to phone GPS
          if (isTracking) {
            console.log('🔄 External GPS disconnected, falling back to phone GPS');
            startPhoneGPS();
          }
        }
      };
      
      readLoop();
      setSource(GPS_SOURCE.EXTERNAL);
      return true;
      
    } catch (err) {
      console.error('Failed to connect external GPS:', err);
      return false;
    }
  }, [handleExternalGPSData, isTracking, startPhoneGPS]);

  /**
   * Disconnect external GPS
   */
  const disconnectExternalGPS = useCallback(async () => {
    if (serialReaderRef.current) {
      try {
        await serialReaderRef.current.cancel();
        serialReaderRef.current.releaseLock();
      } catch (e) {
        console.error('Error releasing reader:', e);
      }
      serialReaderRef.current = null;
    }
    
    if (serialPortRef.current) {
      try {
        await serialPortRef.current.close();
      } catch (e) {
        console.error('Error closing port:', e);
      }
      serialPortRef.current = null;
    }
    
    console.log('🛰️ External GPS disconnected');
  }, []);

  /**
   * Start GPS tracking (tries external first, falls back to phone)
   */
  const startTracking = useCallback(async (preferExternal = true) => {
    setIsTracking(true);
    setTotalDistance(0);
    lastPositionRef.current = null;
    
    if (preferExternal && navigator.serial) {
      // Try external GPS first
      const externalConnected = await connectExternalGPS();
      
      if (externalConnected) {
        // Set up fallback timer - if no external data in 5s, use phone GPS
        externalTimeoutRef.current = setInterval(() => {
          const now = Date.now();
          const lastUpdate = lastExternalUpdateRef.current;
          
          if (!lastUpdate || (now - lastUpdate > 5000)) {
            console.log('⚠️ No external GPS data, falling back to phone GPS');
            startPhoneGPS();
          }
        }, 5000);
        
        return true;
      }
    }
    
    // Fallback to phone GPS
    console.log('📱 Using phone GPS (external not available)');
    return startPhoneGPS();
  }, [connectExternalGPS, startPhoneGPS]);

  /**
   * Stop all GPS tracking
   */
  const stopTracking = useCallback(async () => {
    setIsTracking(false);
    
    // Clear fallback timer
    if (externalTimeoutRef.current) {
      clearInterval(externalTimeoutRef.current);
      externalTimeoutRef.current = null;
    }
    
    // Stop phone GPS
    stopPhoneGPS();
    
    // Disconnect external GPS
    await disconnectExternalGPS();
    
    setSource(GPS_SOURCE.NONE);
  }, [stopPhoneGPS, disconnectExternalGPS]);

  /**
   * Force use phone GPS (bypass external)
   */
  const usePhoneGPSOnly = useCallback(() => {
    disconnectExternalGPS();
    if (externalTimeoutRef.current) {
      clearInterval(externalTimeoutRef.current);
      externalTimeoutRef.current = null;
    }
    setIsTracking(true);
    return startPhoneGPS();
  }, [disconnectExternalGPS, startPhoneGPS]);

  // Listen for legacy NMEA events (USB-Serial path)
  useEffect(() => {
    const handleGPSUpdate = (event) => {
      handleExternalGPSData(event.detail);
    };
    
    window.addEventListener('gps-update', handleGPSUpdate);
    return () => window.removeEventListener('gps-update', handleGPSUpdate);
  }, [handleExternalGPSData]);

  // Listen for CrewZ-case BLE GPS streams.
  // Firmware emits pre-parsed structured updates at 25 Hz so no NMEA decoding
  // is required. Expected detail shape:
  //   { lat, lng, altitude?, speed?, accuracy?, satellites?, timestamp? }
  // Speed is metres-per-second; lat/lng are decimal degrees.
  useEffect(() => {
    const handleCrewZGPS = (event) => {
      const d = event.detail;
      if (!d || typeof d.lat !== 'number' || typeof d.lng !== 'number') return;
      lastExternalUpdateRef.current = Date.now();
      if (source === GPS_SOURCE.PHONE) {
        console.log('🔄 Switching from phone GPS to CrewZ case GPS');
        stopPhoneGPS();
      }
      const next = {
        lat: d.lat,
        lng: d.lng,
        altitude: d.altitude || 0,
        satellites: d.satellites || 0,
        speed: typeof d.speed === 'number' ? d.speed : null,
        source: GPS_SOURCE.EXTERNAL,
      };
      setPosition(next);
      setSource(GPS_SOURCE.EXTERNAL);
      if (typeof d.speed === 'number') setSpeed(d.speed);
      if (typeof d.accuracy === 'number') setAccuracy(d.accuracy);
      setError(null);
      // Distance accumulation (same Haversine + outlier filter as USB-Serial path)
      if (lastPositionRef.current) {
        const dist = calculateDistance(
          lastPositionRef.current.lat,
          lastPositionRef.current.lng,
          next.lat,
          next.lng,
        );
        const movingEnough = typeof next.speed === 'number'
          ? next.speed >= STATIONARY_SPEED_FLOOR_MPS
          : true;
        if (dist < 50 && movingEnough) setTotalDistance(prev => prev + dist);
      }
      lastPositionRef.current = next;
    };
    window.addEventListener('crewz-gps-update', handleCrewZGPS);
    return () => window.removeEventListener('crewz-gps-update', handleCrewZGPS);
  }, [source, stopPhoneGPS]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTracking();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // Current state
    position,
    source,
    isTracking,
    error,
    accuracy,
    speed,
    totalDistance,
    
    // Source checks
    isExternalGPS: source === GPS_SOURCE.EXTERNAL,
    isPhoneGPS: source === GPS_SOURCE.PHONE,
    hasGPS: source !== GPS_SOURCE.NONE,
    
    // API support checks
    supportsExternalGPS: typeof navigator !== 'undefined' && 'serial' in navigator,
    supportsPhoneGPS: typeof navigator !== 'undefined' && 'geolocation' in navigator,
    
    // Controls
    startTracking,
    stopTracking,
    connectExternalGPS,
    usePhoneGPSOnly,
    // Reset both the cumulative distance counter AND the last-position
    // anchor — otherwise the very first GPS sample after pressing Start
    // would Haversine against a stale anchor from the previous session
    // and inject a phantom distance jump (Brief B).
    resetDistance: () => {
      setTotalDistance(0);
      lastPositionRef.current = null;
    },
    
    // Constants
    GPS_SOURCE
  };
}

export default useGPS;
